import React, {useCallback, useEffect, useState} from 'react';
import styles from "./styles.module.scss";
import PagePanel from "app/components/PagePanel";
import {useTranslation} from "react-i18next";
import UserItem from "app/routes/FacilityUsers/UserItem";
import AddFacilityUser from "app/routes/FacilityUsers/AddFacilityUser";
import {fetchDelete, fetchGet, fetchPost, fetchPut} from "app/utils/request";
import {toast} from "react-toastify";
import useCurrentFacility from 'app/utils/hooks/scheduling/useCurrentFacility';
import { Loading } from 'app/components/Wrappers';
import { schedulingFetchGet } from 'app/utils/request/scheduling/fetch';
import Tabs from 'app/components/Tabs';
import { useAdminSettingsTabs } from "app/routes/Facility/adminSettingsTabs";
import ButtonVariant from 'app/components/Button/ButtonVariant';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import applyUserEdit from "app/routes/FacilityUsers/applyUserEdit";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  title?: string;
  email: string;
  role: string;
  isAdmin: boolean;
}

const FacilityUsers = () => {
  const { t } = useTranslation();
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const currentFacility = useCurrentFacility();
  const tabs = useAdminSettingsTabs("users");
  const [users, setUsers] = useState([]);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderedUsers, setReorderedUsers] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const openAddModal = () => setAddModalOpen(true);
  const closeAddModal = () => setAddModalOpen(false);

  const facilityId = currentFacility.data?.id;

  useEffect(() => {
    if(!facilityId) { return; }
    fetchGet(`/facilities/${facilityId}/memberships`, {}, {version: 1})
      .then((response) => {
        setUsers(response);
      }).catch((error) => {
        toast.error("Failed to fetch users");
        console.error(error);
      });
  }, [facilityId]);

  const handleAddUser = (user: User) => {
    fetchPost(`/facilities/${facilityId}/memberships`, { membership: user }, {version: 1})
      .then((response) => {
        setUsers([...users, response]);
        toast.success("User added successfully");
      }).catch((error) => {
        toast.error("Failed to add user");
        console.error(error);
      });
  };

  const checkCoachRemoval = (coach: User) => {
    if (coach.role !== 'coach') {
      return Promise.resolve();
    }

    return schedulingFetchGet(`/coach_profiles/${coach.id}/removable`);
  };

  const handleRemoveUser = (user: User) => {
    checkCoachRemoval(user)
      .then(() => {
        return fetchDelete(`/facilities/${facilityId}/memberships/${user.id}`, {}, {version: 1})
        .then(() => {
          setUsers(users.filter((u) => u.id !== user.id));
          toast.success("User removed successfully");
        }).catch((error) => {
          toast.error("Failed to remove user");
          console.error(error);
        });
      })
      .catch((err) => {
        if (err.status === 409) {
          toast.error(t("Coach is booked for future programs and cannot be deleted"));
        } else {
          toast.error("Failed to remove user");
        }
      });
  };

  const handleEditUser = (user: User) => {
    fetchPut(`/facilities/${facilityId}/memberships/${user.id}`, { membership: user }, {version: 1})
      .then(() => {
        // CFTB-3447: laid over the member rather than replacing them — see the module.
        setUsers((prevUsers) => applyUserEdit(prevUsers, user));
        toast.success("User updated successfully");
      }).catch((error) => {
        toast.error("Failed to update user");
        console.error(error);
      });
  };

  const handleEnterReorderMode = () => {
    setReorderedUsers([...users]);
    setIsReorderMode(true);
  };

  const handleOnDragEnd = useCallback((result: any) => {
    if (!result.destination) { return; }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    const items = [...reorderedUsers];
    const [reorderedItem] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, reorderedItem);
    setReorderedUsers(items);
  }, [reorderedUsers]);

  const handleSaveOrder = useCallback(async () => {
    if (isSaving) { return; }
    setIsSaving(true);

    const memberships: Record<string, number> = {};
    reorderedUsers.forEach((u: any, index: number) => {
      memberships[u.id] = index + 1;
    });

    try {
      await fetchPost(
        `/facilities/${facilityId}/memberships/sort`,
        { memberships },
        { version: 1 }
      );
      setUsers([...reorderedUsers]);
      setIsReorderMode(false);
      toast.success(t("Order saved successfully"));
    } catch (error) {
      toast.error(t("Failed to save order"));
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  }, [reorderedUsers, facilityId, isSaving]);

  const handleCancelReorder = () => {
    setIsReorderMode(false);
    setReorderedUsers([]);
  };

  return (
    <div className="admin-settings-container">
      <div className={styles.title}>
        <h1>{t("Admin Settings")}</h1>
      </div>
      <div>
        <div className={styles.tabs_container}>
          <Tabs tabs={tabs} scrollable />
        </div>
        <PagePanel
          title={t("Users")}
          titleStyles={styles.header}
          headerChildren={(
            <div className={styles.page_panel_header}>
              <ButtonVariant
                title={isReorderMode ? t("Save") : t("Reorder")}
                buttonType={isReorderMode ? "primary" : "secondary-outline"}
                onClickFunc={isReorderMode ? handleSaveOrder : handleEnterReorderMode}
              />
              {isReorderMode && (
                <ButtonVariant
                  title={t("Cancel")}
                  buttonType="secondary-outline"
                  onClickFunc={handleCancelReorder}
                />
              )}
              {!isReorderMode && <ButtonVariant
                title={t("Add User")}
                buttonType="primary-outline"
                onClickFunc={openAddModal}
                icon={<i className="ico ico-add" />}
                childStyles={styles.add_button_icon}
              />}
            </div>
          )}>
          {!facilityId ? (
            <Loading isLoading={!facilityId} loadType="spinner" />
          ) : isReorderMode ? (
            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable droppableId="facility-users">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {reorderedUsers.map((user: any, index: number) => (
                      <Draggable
                        key={user.id}
                        draggableId={user.id}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={styles.draggable_item}
                            style={provided.draggableProps.style}
                          >
                            <UserItem
                              user={user}
                              onRemove={handleRemoveUser}
                              onEdit={handleEditUser}
                              isReorderMode
                              dragHandleProps={provided.dragHandleProps}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            users.map((user) => (
              <UserItem
                key={user.id}
                user={user}
                onRemove={handleRemoveUser}
                onEdit={handleEditUser}
              />
            ))
          )}
        </PagePanel>
        <AddFacilityUser
          isOpen={isAddModalOpen}
          onClose={closeAddModal}
          users={users}
          onAddUser={handleAddUser}
        />
      </div>
    </div>
  );
};

export default FacilityUsers;
